/*
  SELF Katalog (zakladki) - sections/self-catalog-tabs.liquid

  Serwerowo przychodzi tylko pierwsza partia pierwszej zakladki. Reszta dociagana
  jest przez Section Rendering API pod adresem kolekcji:
    /collections/<handle>?page=<n>&section_id=<id>
  Sekcja w kontekscie kolekcji renderuje sam fragment (element [data-sct-fragment]).

  Dwa tryby dokladania (ustawienie sekcji):
  - auto: IntersectionObserver na czujniku pod siatka dokłada partie w trakcie
    przewijania, do limitu z ustawien. Po limicie wraca przycisk, zeby strona nie
    rosla bez konca. Bez IntersectionObserver dziala sam przycisk.
  - button: klasyczny przycisk.

  Gdy nie ma czego dokladac, w miejscu przycisku pojawia sie powrot na gore sekcji
  (scrollIntoView respektuje scroll-margin-top, wiec sticky header nie zaslania).
*/
class SelfCatalogTabs extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.loadMode = this.dataset.loadMode || 'button';
    this.autoloadMax = parseInt(this.dataset.autoloadMax, 10) || 0;
    this.autoCapable = 'IntersectionObserver' in window;

    this.tabs = Array.from(this.querySelectorAll('.sct__tab'));
    this.panels = Array.from(this.querySelectorAll('.sct__panel'));
    this.observers = [];
    this.watched = new WeakMap();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.handle));
    });

    const tablist = this.querySelector('.sct__tabs');
    if (tablist) tablist.addEventListener('keydown', (event) => this.onKeydown(event));

    this.panels.forEach((panel) => {
      const more = panel.querySelector('[data-sct-more]');
      if (more) more.addEventListener('click', () => this.loadMore(panel, false));

      const back = panel.querySelector('[data-sct-back]');
      if (back) back.addEventListener('click', () => this.backToTop());

      this.syncFoot(panel);
      this.observe(panel);
    });
  }

  disconnectedCallback() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }

  panelFor(handle) {
    return this.panels.find((panel) => panel.dataset.handle === handle);
  }

  /* Czujnik w ukrytym panelu nie raportuje przeciecia (display: none), wiec
     obserwator sam milczy dla nieaktywnych zakladek - nie trzeba go przelaczac. */
  observe(panel) {
    if (this.loadMode !== 'auto' || !this.autoCapable) return;

    const sentinel = panel.querySelector('[data-sct-sentinel]');
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.autoLoad(panel);
        });
      },
      { rootMargin: '600px 0px 600px 0px' }
    );
    observer.observe(sentinel);
    this.observers.push(observer);
    this.watched.set(panel, { observer: observer, sentinel: sentinel });
  }

  /*
    IntersectionObserver raportuje ZMIANE stanu przeciecia. Jesli po dolozeniu
    partii czujnik nadal siedzi w kadrze, zadne nowe zdarzenie nie przyjdzie
    i doladowywanie staje az do kolejnego ruchu palcem. Ponowne observe()
    wymusza swiezy raport dla aktualnego stanu.
  */
  rearm(panel) {
    const watch = this.watched.get(panel);
    if (!watch) return;
    watch.observer.unobserve(watch.sentinel);
    watch.observer.observe(watch.sentinel);
  }

  autoLoad(panel) {
    const grid = panel.querySelector('[data-sct-grid]');
    if (!grid || !grid.dataset.nextPage) return;

    // po nieudanym doladowaniu nie prubujemy dalej sami, zeby nie mlocic
    // serwera w petli; uzytkownik dostaje przycisk i decyduje
    if (grid.dataset.failed === '1') return;

    const used = parseInt(grid.dataset.autoloaded, 10) || 0;
    if (used >= this.autoloadMax) {
      this.syncFoot(panel);
      return;
    }

    this.loadMore(panel, true);
  }

  select(handle) {
    const panel = this.panelFor(handle);
    if (!panel || panel.hidden === false) return;

    this.tabs.forEach((tab) => {
      const on = tab.dataset.handle === handle;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
    this.panels.forEach((item) => {
      item.hidden = item !== panel;
    });

    const grid = panel.querySelector('[data-sct-grid]');
    // pierwsze wejscie w zakladke nie zjada budzetu automatycznych partii
    if (grid && grid.children.length === 0) this.loadMore(panel, false);
  }

  onKeydown(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const step = event.key === 'ArrowRight' ? 1 : this.tabs.length - 1;
    const next = this.tabs[(current + step) % this.tabs.length];
    this.select(next.dataset.handle);
    next.focus();
  }

  async loadMore(panel, fromObserver) {
    const grid = panel.querySelector('[data-sct-grid]');
    if (!grid || grid.dataset.loading === '1' || !grid.dataset.nextPage) return;

    const page = grid.dataset.nextPage;
    const button = panel.querySelector('[data-sct-more]');
    const loading = panel.querySelector('[data-sct-loading]');

    grid.dataset.loading = '1';
    if (button) button.setAttribute('aria-busy', 'true');
    if (loading) loading.hidden = false;
    this.clearError(panel);

    const url = `${panel.dataset.url}?page=${page}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const fragment = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[data-sct-fragment]');
      if (!fragment) throw new Error('brak fragmentu');

      while (fragment.firstElementChild) grid.appendChild(fragment.firstElementChild);

      grid.dataset.nextPage = fragment.dataset.nextPage || '';
      grid.dataset.failed = '0';
      if (fromObserver) {
        grid.dataset.autoloaded = String((parseInt(grid.dataset.autoloaded, 10) || 0) + 1);
      }
      this.syncFoot(panel, fragment.dataset.total);
      // rearm TYLKO po sukcesie - w finally zrobilby petle nieudanych zapytan
      if (grid.dataset.nextPage) this.rearm(panel);
    } catch (error) {
      grid.dataset.failed = '1';
      this.showError(panel);
      this.syncFoot(panel);
      console.error('[self-catalog-tabs]', error);
    } finally {
      grid.dataset.loading = '0';
      if (button) button.removeAttribute('aria-busy');
      if (loading) loading.hidden = true;
    }
  }

  syncFoot(panel, totalFromFragment) {
    const grid = panel.querySelector('[data-sct-grid]');
    const status = panel.querySelector('[data-sct-status]');
    const button = panel.querySelector('[data-sct-more]');
    const back = panel.querySelector('[data-sct-back]');
    const total = totalFromFragment || panel.dataset.total;

    // liczymy karty produktow, kafel edytorski nie jest produktem
    const shown = grid.querySelectorAll('.sct__cell:not(.sct__cell--promo)').length;
    const hasNext = Boolean(grid.dataset.nextPage);
    const budgetLeft = (parseInt(grid.dataset.autoloaded, 10) || 0) < this.autoloadMax;
    const failed = grid.dataset.failed === '1';
    const autoWillHandleIt = this.loadMode === 'auto' && this.autoCapable && budgetLeft && !failed;

    if (status) status.textContent = shown > 0 ? `${shown} z ${total}` : '';
    if (button) button.hidden = !hasNext || autoWillHandleIt;
    if (back) back.hidden = hasNext || shown === 0;
  }

  backToTop() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    const active = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    if (active) active.focus({ preventScroll: true });
  }

  clearError(panel) {
    const existing = panel.querySelector('.sct__error');
    if (existing) existing.remove();
  }

  showError(panel) {
    if (panel.querySelector('.sct__error')) return;
    const note = document.createElement('p');
    note.className = 'sct__error';
    note.textContent = 'Nie udało się dociągnąć produktów. Odśwież stronę albo otwórz całą kategorię.';
    const foot = panel.querySelector('.sct__foot');
    if (foot) foot.appendChild(note);
  }
}

if (!customElements.get('self-catalog-tabs')) {
  customElements.define('self-catalog-tabs', SelfCatalogTabs);
}
